import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useSetRecoilState } from "recoil";
import { useNavigate, useLocation } from "react-router-dom";
import { FiArrowUp, FiChevronDown, FiChevronUp, FiDownload, FiMaximize2, FiMinus } from "react-icons/fi";
import { HiOutlineDocumentText } from "react-icons/hi";
import {
  WidgetContainer,
  WidgetBubble,
  WidgetHeader,
  WidgetLogo,
  WidgetTitle,
  WidgetActions,
  WidgetIconButton,
  WidgetBody,
  WidgetMessage,
  WidgetMessageBubble,
  WidgetMiniAvatar,
  WidgetInputRow,
  WidgetInput,
  WidgetSendButton,
  DocumentMessageWrapper,
  DocumentMessageIconWrapper,
  DocumentMessageName,
  DocumentDownloadButton,
  SuggestionsTitle,
  SuggestionsSpark,
  SuggestionChips,
  SuggestionChip,
  SuggestionChipDot,
  SuggestionChipText,
  SuggestionChipArrow,
  WidgetSize,
} from "./Styles";
import { dueDiligenceChatActionAtom } from "../../Common/State/dueDiligenceChatAction";
import { openChatIdAtom } from "../../Common/State/openChatId";
import { eventBus } from "../../Common/EventBus";
import { useChat } from "../../Common/Hooks/useChat";
import { useDueDiligence } from "../../Common/Hooks/useDueDiligence";
import { usePlatformConfig } from "../../Common/Hooks/usePlatformConfig";
import { isErr } from "../../Common/Utils/Result";
import { openSseStream } from "../../Common/Utils/openSseStream";
import { authState } from "../../Common/State/Auth";
import { Chat as ChatDTO } from "../../Common/Types-DTOs/Chat";
import { MessageDTOType, MessageSenderType as Sender } from "../../Common/Types-DTOs/Message";
import { FormattedText } from "../FormattedText";
import { ChatCreditModal, isChatCreditError } from "../ChatCreditModal";
import { ThinkingWrapper } from "../ProcessingMessage";
import { MiniThinkingRing, MiniThinkingText } from "./Styles";
import { SUGGESTION_RISK, type SuggestionRisk } from "@dd-report-components";
const MikeLogo = "/mike_logo.png";
import UserIcon from "../../Assets/User-icon.png";
import { caseAliasFromAction } from "../../Common/Utils/caseAliasFromAction";
import { reportPath } from "../../Common/Utils/reportAnchor";

const SHORT_INTERVAL = 500;
const LONG_INTERVAL = 30000;

export const MiniChatWidget = () => {
  const { t } = useTranslation();
  const [ddAction, setDdAction] = useRecoilState(dueDiligenceChatActionAtom);
  const [auth] = useRecoilState(authState);
  const navigate = useNavigate();
  const location = useLocation();
  const { createChat, getChatById, sendMessage } = useChat();
  const { getChatSuggestions } = useDueDiligence();
  const platformConfig = usePlatformConfig();

  const ddCaseIdFromUrl = (() => {
    const match = location.pathname.match(/\/due-diligence\/case\/([^/]+)/);
    return match?.[1] ?? undefined;
  })();
  const isOnChatPage = location.pathname === "/" || location.pathname.startsWith("/chat");
  // Where the chatbot's "Fuente: Sección N, Bloque X" links lead: the report of
  // the case the reader has open, from the report itself or the case page.
  const ddReportPath = ddCaseIdFromUrl ? reportPath(ddCaseIdFromUrl) : undefined;

  const [chat, setChat] = useState<ChatDTO | undefined>();
  const [inputValue, setInputValue] = useState("");
  const [isMinimized, setIsMinimized] = useState(() => !ddAction?.chatId);
  const [widgetSize, setWidgetSize] = useState<WidgetSize>("small");
  const [thinkingText, setThinkingText] = useState<string | null>(null);
  const [creditModalMessage, setCreditModalMessage] = useState<string | null>(null);
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(null);
  const [initialSuggestions, setInitialSuggestions] = useState<
    { text: string; risk: SuggestionRisk }[]
  >([]);
  const [followUpSuggestions, setFollowUpSuggestions] = useState<string[]>([]);
  const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeStreamRef = useRef<{ close: () => void } | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  // Prevents the chatId useEffect from re-fetching when we just created the chat locally
  const skipNextChatFetchRef = useRef(false);
  // Guards handleSend against a second Enter/click firing before the first network round-trip resolves
  const isSendingRef = useRef(false);

  const chatId = ddAction?.chatId;
  const setOpenChatId = useSetRecoilState(openChatIdAtom);

  // Lets the push listener know this chat is already visible to the user, so
  // it can skip the "Mike ha respondido" toast — this chat lives in Recoil
  // state, not in the URL, so the listener has no other way to detect it.
  useEffect(() => {
    if (!chatId || isMinimized) return;
    setOpenChatId(chatId);
    return () => setOpenChatId((prev) => (prev === chatId ? null : prev));
  }, [chatId, isMinimized, setOpenChatId]);

  useEffect(() => {
    if (!chatId) {
      setChat(undefined);
      return;
    }

    setIsMinimized(false);

    // Skip fetch when we just created the chat and already have it in local state
    if (skipNextChatFetchRef.current) {
      skipNextChatFetchRef.current = false;
      return;
    }

    getChatById(chatId).then((res) => {
      if (!isErr(res)) setChat(res.value.chat);
    });

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [chatId]);

  useEffect(() => {
    if (!ddCaseIdFromUrl) {
      setInitialSuggestions([]);
      return;
    }
    let cancelled = false;
    getChatSuggestions(ddCaseIdFromUrl).then((res) => {
      if (!cancelled && !isErr(res)) setInitialSuggestions(res.value.suggestions);
    });
    return () => {
      cancelled = true;
    };
  }, [ddCaseIdFromUrl]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [chat?.messages.length]);

  const startPolling = (id: string, initialLen: number) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    let botResponded = false;

    const fastId = setInterval(async () => {
      const res = await getChatById(id);
      if (isErr(res)) return;
      const updated = res.value.chat;
      if (updated.messages.length !== initialLen) {
        setChat(updated);
        const last = updated.messages[updated.messages.length - 1];
        if (last?.sender === Sender.BOT && !botResponded) {
          botResponded = true;
          clearInterval(fastId);

          if (last.action?.type === "NAVIGATE_TO_DD_CASE") {
            const caseAlias = caseAliasFromAction(last.action.data);
            if (caseAlias) {
              navigate(`/due-diligence/case/${caseAlias}/report`);
              return;
            }
          } else if (last.action?.type === "NAVIGATE_TO_DD_LIST") {
            navigate("/due-diligence");
            return;
          } else if (last.action?.type === "NAVIGATE_TO_CONTRACT") {
            const contractId = last.action.data?.["contractId"] as string | undefined;
            if (contractId) {
              navigate(`/contracts/${contractId}`);
              return;
            }
          } else if (last.action?.type === "NAVIGATE_TO_CONTRACT_LIST") {
            navigate("/contracts");
            return;
          } else if (last.action?.type === "CONTRACT_VARIABLES_UPDATED") {
            const contractId = last.action.data?.["contractId"] as string | undefined;
            if (contractId) eventBus.emit("CONTRACT_VARIABLES_UPDATED", { contractId });
          } else if (last.action?.type === "SUGGESTED_FOLLOWUP_QUESTIONS") {
            const suggestions = (last.action.data?.["suggestions"] as string[] | undefined) ?? [];
            setFollowUpSuggestions(suggestions);
          }

          const slowId = setInterval(async () => {
            const r = await getChatById(id);
            if (!isErr(r)) setChat(r.value.chat);
          }, LONG_INTERVAL);
          intervalRef.current = slowId;
        }
      }
    }, SHORT_INTERVAL);
    intervalRef.current = fastId;
  };

  const startSseStream = (targetChatId: string, userMessageId: string, initialLen: number) => {
    const tempBotId = `streaming-${Date.now()}`;
    setChat((prev) =>
      prev
        ? {
            ...prev,
            messages: [
              ...prev.messages,
              {
                id: tempBotId,
                messageType: MessageDTOType.TEXT,
                text: "",
                sender: Sender.BOT,
                creationDate: new Date(),
              },
            ],
          }
        : prev
    );

    activeStreamRef.current = openSseStream(targetChatId, userMessageId, {
      onThought: (text) => {
        setThinkingText(text);
      },
      onDelta: (delta) => {
        setThinkingText(null);
        setChat((prev) =>
          prev
            ? {
                ...prev,
                messages: prev.messages.map((m) =>
                  m.id === tempBotId ? { ...m, text: (m.text ?? "") + delta } : m
                ),
              }
            : prev
        );
      },
      onDone: (botMessageId, fullText, creationDate, action, documentUrl, documentName) => {
        setChat((prev) =>
          prev
            ? {
                ...prev,
                messages: [
                  ...prev.messages.map((m) =>
                    m.id === tempBotId
                      ? { ...m, id: botMessageId, text: fullText, creationDate }
                      : m
                  ),
                  ...(documentUrl
                    ? [
                        {
                          id: `${botMessageId}-document`,
                          messageType: MessageDTOType.DOCUMENT,
                          documentUrl,
                          documentName: documentName ?? "",
                          sender: Sender.BOT,
                          creationDate,
                        },
                      ]
                    : []),
                ],
              }
            : prev
        );
        activeStreamRef.current = null;
        setThinkingText(null);

        const navigateCaseAlias =
          action?.type === "NAVIGATE_TO_DD_CASE" ? caseAliasFromAction(action.data) : undefined;

        if (navigateCaseAlias) {
          setDdAction((prev) => prev ? { ...prev, miniWidgetVisible: true } : prev);
          navigate(`/due-diligence/case/${navigateCaseAlias}/report`);
        } else if (action?.type === "NAVIGATE_TO_DD_LIST") {
          navigate("/due-diligence");
        } else if (action?.type === "NAVIGATE_TO_CONTRACT" && action.data?.["contractId"]) {
          navigate(`/contracts/${action.data["contractId"] as string}`);
        } else if (action?.type === "NAVIGATE_TO_CONTRACT_LIST") {
          navigate("/contracts");
        } else if (action?.type === "CONTRACT_VARIABLES_UPDATED" && action.data?.["contractId"]) {
          eventBus.emit("CONTRACT_VARIABLES_UPDATED", {
            contractId: action.data["contractId"] as string,
          });
        } else if (action?.type === "SUGGESTED_FOLLOWUP_QUESTIONS") {
          setFollowUpSuggestions((action.data?.["suggestions"] as string[] | undefined) ?? []);
        }
      },
      onError: () => {
        setChat((prev) =>
          prev
            ? {
                ...prev,
                messages: prev.messages.filter((m) => m.id !== tempBotId),
              }
            : prev
        );
        activeStreamRef.current = null;
        setThinkingText(null);
        startPolling(targetChatId, initialLen);
      },
    }, auth.token);
  };

  const buildDdAgentContext = (): Record<string, unknown> | undefined => {
    if (!ddCaseIdFromUrl) return undefined;
    return { activeDueDiligenceCaseId: ddCaseIdFromUrl };
  };

  const handleSend = async (overrideText?: string) => {
    const raw = overrideText ?? inputValue;
    if (!raw.trim()) return;
    if (isSendingRef.current) return;
    isSendingRef.current = true;
    const msg = raw.trim();
    if (!overrideText) setInputValue("");
    setFollowUpSuggestions([]);

    try {
      if (activeStreamRef.current) {
        activeStreamRef.current.close();
        activeStreamRef.current = null;
      }

      if (!chatId) {
        // Show the user message immediately before the network round-trip
        setPendingUserMessage(msg);
        const createResponse = await createChat(msg, buildDdAgentContext());
        setPendingUserMessage(null);
        if (isErr(createResponse)) return;
        const newChat = createResponse.value.chat;
        const userMessageId = createResponse.value.userMessageId;
        // Flag the useEffect so it doesn't re-fetch and overwrite our streaming state
        skipNextChatFetchRef.current = true;
        // ddAction defaults to null until ChatView ever sets it, so a widget-only
        // session (never opened /chat) must seed a fresh state here instead of
        // discarding the chatId — otherwise it never persists and every
        // following message re-enters this branch, creating a brand new chat
        // instead of continuing the conversation (confirmed live: MIKE-290).
        setDdAction((prev) =>
          prev
            ? { ...prev, chatId: newChat.id }
            : { chatId: newChat.id, data: {}, miniWidgetVisible: true }
        );
        setChat(newChat);
        if (!platformConfig.chatSseEnabled || !userMessageId) {
          startPolling(newChat.id, newChat.messages.length);
        } else {
          startSseStream(newChat.id, userMessageId, newChat.messages.length);
        }
        return;
      }

      if (!chat) return;
      const targetChatId = chat.id;

      let appendedLen = chat.messages.length;
      setChat((prev) => {
        if (!prev) return prev;
        const messages = [
          ...prev.messages,
          {
            id: "",
            messageType: MessageDTOType.TEXT,
            text: msg,
            sender: Sender.USER,
            creationDate: new Date(),
          },
        ];
        appendedLen = messages.length;
        return { ...prev, messages };
      });

      const response = await sendMessage(targetChatId, msg, buildDdAgentContext());

      if (isErr(response) && isChatCreditError(response.error)) {
        setCreditModalMessage(response.error.message);
        return;
      }

      const userMessageId = !isErr(response) ? response.value.userMessageId : undefined;

      if (!platformConfig.chatSseEnabled || !userMessageId) {
        startPolling(targetChatId, appendedLen);
        return;
      }

      startSseStream(targetChatId, userMessageId, appendedLen);
    } finally {
      isSendingRef.current = false;
    }
  };

  // Paints the message as a bot bubble in local state only (the backend has not stored anything)
  const handleCreditModalDismiss = () => {
    if (creditModalMessage) {
      const dismissedMessage = creditModalMessage;
      setChat((prev) =>
        prev
          ? {
              ...prev,
              messages: [
                ...prev.messages,
                {
                  id: `local-${Date.now()}`,
                  messageType: MessageDTOType.TEXT,
                  text: dismissedMessage,
                  sender: Sender.BOT,
                  creationDate: new Date(),
                },
              ],
            }
          : prev
      );
    }
    setCreditModalMessage(null);
  };

  const handleClose = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (activeStreamRef.current) {
      activeStreamRef.current.close();
      activeStreamRef.current = null;
    }
    // The widget is visible by default everywhere except /chat*, so closing it
    // has to record an explicit "hidden" choice — resetting to null would just
    // read as "not yet activated" and the widget would reappear immediately.
    setDdAction({ data: {}, miniWidgetVisible: false });
    setChat(undefined);
  };

  const handleExpand = () => {
    if (chat?.id) navigate(`/chat/${chat.id}`);
    handleClose();
  };

  // Visible by default on every page except the full chat view — hidden only
  // when the user explicitly closed it (see handleClose).
  if (isOnChatPage || ddAction?.miniWidgetVisible === false) return null;

  const isLoading =
    chat && chat.messages.length > 0 &&
    chat.messages[chat.messages.length - 1].sender === Sender.USER;

  if (isMinimized) {
    return (
      <WidgetBubble onClick={() => setIsMinimized(false)} title={t("chat.miniChatWidget.openChatTitle")} data-testid="chat-bubble">
        <img src={MikeLogo} alt={t("chat.miniChatWidget.mikeLogoAlt")} />
      </WidgetBubble>
    );
  }

  return (
    <>
    <ChatCreditModal
      message={creditModalMessage}
      onDismiss={handleCreditModalDismiss}
      onBuy={() => setCreditModalMessage(null)}
    />
    <WidgetContainer data-testid="chat-widget" $size={widgetSize}>
      <WidgetHeader>
        <WidgetLogo>
          <img src={MikeLogo} alt={t("chat.miniChatWidget.mikeLogoAlt")} />
        </WidgetLogo>
        <WidgetTitle>{t("chat.miniChatWidget.title")}</WidgetTitle>
        <WidgetActions>
          <WidgetIconButton onClick={handleExpand} title={t("chat.miniChatWidget.expandChatTitle")}>
            <FiMaximize2 size={15} />
          </WidgetIconButton>
          {widgetSize === "small" ? (
            <WidgetIconButton onClick={() => setWidgetSize("medium")} title={t("chat.miniChatWidget.enlargeChatTitle")}>
              <FiChevronUp size={15} />
            </WidgetIconButton>
          ) : (
            <WidgetIconButton onClick={() => setWidgetSize("small")} title={t("chat.miniChatWidget.shrinkChatTitle")}>
              <FiChevronDown size={15} />
            </WidgetIconButton>
          )}
          <WidgetIconButton onClick={() => setIsMinimized(true)} title={t("chat.miniChatWidget.minimizeTitle")}>
            <FiMinus size={15} />
          </WidgetIconButton>
        </WidgetActions>
      </WidgetHeader>

      <WidgetBody ref={bodyRef}>
        {pendingUserMessage ? (
          <>
            <WidgetMessage $isBot={false}>
              <WidgetMiniAvatar $isBot={false}>
                <img src={UserIcon} alt="" />
              </WidgetMiniAvatar>
              <WidgetMessageBubble $isBot={false}>
                {pendingUserMessage}
              </WidgetMessageBubble>
            </WidgetMessage>
            <WidgetMessage $isBot>
              <WidgetMiniAvatar $isBot>
                <img src={MikeLogo} alt="" />
              </WidgetMiniAvatar>
              <WidgetMessageBubble $isBot>
                <ThinkingWrapper><MiniThinkingRing /></ThinkingWrapper>
              </WidgetMessageBubble>
            </WidgetMessage>
          </>
        ) : !chat && chatId ? (
          <WidgetMessage $isBot>
            <WidgetMiniAvatar $isBot>
              <img src={MikeLogo} alt="" />
            </WidgetMiniAvatar>
            <WidgetMessageBubble $isBot>
              <ThinkingWrapper>
                <MiniThinkingRing />
              </ThinkingWrapper>
            </WidgetMessageBubble>
          </WidgetMessage>
        ) : chat ? (
          chat.messages.map((msg, i) => {
            const isBot = msg.sender === Sender.BOT;
            const text = msg.text ?? "";
            const isDocument = msg.messageType === MessageDTOType.DOCUMENT;
            return (
              <WidgetMessage key={i} $isBot={isBot}>
                <WidgetMiniAvatar $isBot={isBot}>
                  <img src={isBot ? MikeLogo : UserIcon} alt="" />
                </WidgetMiniAvatar>
                <WidgetMessageBubble $isBot={isBot}>
                  {isBot && isDocument ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      {text && <FormattedText text={text} reportPath={ddReportPath} />}
                      <DocumentMessageWrapper>
                        <DocumentMessageIconWrapper>
                          <HiOutlineDocumentText />
                        </DocumentMessageIconWrapper>
                        <DocumentMessageName>{msg.documentName}</DocumentMessageName>
                        {msg.documentUrl && (
                          <DocumentDownloadButton
                            onClick={() => window.open(msg.documentUrl, "_blank")}
                          >
                            <FiDownload />
                          </DocumentDownloadButton>
                        )}
                      </DocumentMessageWrapper>
                    </div>
                  ) : isBot && text === "" ? (
                    <ThinkingWrapper>
                      <MiniThinkingRing />
                      {thinkingText && <MiniThinkingText>{thinkingText}</MiniThinkingText>}
                    </ThinkingWrapper>
                  ) : isBot ? (
                    <FormattedText text={text} reportPath={ddReportPath} />
                  ) : (
                    text
                  )}
                </WidgetMessageBubble>
              </WidgetMessage>
            );
          })
        ) : null}
        {!!ddCaseIdFromUrl &&
          initialSuggestions.length > 0 &&
          !pendingUserMessage &&
          (!chat || chat.messages.length === 0) && (
            <>
              <SuggestionsTitle>
                <SuggestionsSpark>✦</SuggestionsSpark> {t("chat.miniChatWidget.suggestionsTitle")}
              </SuggestionsTitle>
              <SuggestionChips>
                {initialSuggestions.map((s, i) => (
                  <SuggestionChip key={i} $risk={s.risk} onClick={() => handleSend(s.text)}>
                    <SuggestionChipDot $risk={s.risk} />
                    <SuggestionChipText>{s.text}</SuggestionChipText>
                    <SuggestionChipArrow $risk={s.risk}>↗</SuggestionChipArrow>
                  </SuggestionChip>
                ))}
              </SuggestionChips>
            </>
          )}
        {followUpSuggestions.length > 0 && !isLoading && !pendingUserMessage && (
          <SuggestionChips $inline>
            {followUpSuggestions.map((text, i) => (
              <SuggestionChip key={i} $risk={SUGGESTION_RISK.neutral} onClick={() => handleSend(text)}>
                <SuggestionChipDot $risk={SUGGESTION_RISK.neutral} />
                <SuggestionChipText>{text}</SuggestionChipText>
                <SuggestionChipArrow $risk={SUGGESTION_RISK.neutral}>↗</SuggestionChipArrow>
              </SuggestionChip>
            ))}
          </SuggestionChips>
        )}
        {isLoading && (
          <WidgetMessage $isBot>
            <WidgetMiniAvatar $isBot>
              <img src={MikeLogo} alt="" />
            </WidgetMiniAvatar>
            <WidgetMessageBubble $isBot>
              <ThinkingWrapper>
                <MiniThinkingRing />
              </ThinkingWrapper>
            </WidgetMessageBubble>
          </WidgetMessage>
        )}
      </WidgetBody>

      <WidgetInputRow>
        <WidgetInput
          placeholder={t("chat.miniChatWidget.inputPlaceholder")}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && inputValue.trim() && !isLoading && !pendingUserMessage) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <WidgetSendButton
          onClick={() => handleSend()}
          disabled={!inputValue.trim() || !!isLoading || !!pendingUserMessage}
        >
          <FiArrowUp size={14} />
        </WidgetSendButton>
      </WidgetInputRow>
    </WidgetContainer>
    </>
  );
};
